import {
  BaseResourceConfig,
  isListingResourceConfig,
  ListingResourceConfig,
} from './types';

/**
 * Updates the timestamp field on an object if applicable
 */
export function updateTimestamp<T extends Record<string, any>>(
  resource: T,
  config: BaseResourceConfig,
  isCwa: boolean = false,
): T {
  if (isListingResourceConfig(config)) {
    const modificationTimestampField = getTimestampField(config, isCwa);

    if (modificationTimestampField && !resource[modificationTimestampField]) {
      return {
        ...resource,
        [modificationTimestampField]: new Date().toISOString(),
      };
    }
  }

  return resource;
}

/**
 * Get the appropriate timestamp field name
 */
export function getTimestampField(
  config: ListingResourceConfig,
  isCwa: boolean = false,
): string | undefined {
  if (!config.modificationTimestampField) return undefined;

  return isCwa && config.supportsCwa
    ? `CWA${config.modificationTimestampField}`
    : config.modificationTimestampField;
}

/**
 * Adds the CWA LIS key to a resource when in CWA mode
 */
export function addCwaLisKey<T extends Record<string, any>>(
  resource: T,
  config: BaseResourceConfig,
  lisKey: string,
): T {
  if (!isListingResourceConfig(config) || !config.lisKeyField) {
    return resource;
  }

  return {
    ...resource,
    [config.lisKeyField]: lisKey,
  };
}

/**
 * Add CWA prefix to entity name and fields
 */
export function addCwaPrefix(data: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    // Skip fields that already have CWA prefix
    if (key.startsWith('CWA')) {
      result[key] = value;
      continue;
    }

    // Add CWA prefix to all other fields
    result[`CWA${key}`] = value;
  }

  return result;
}

/**
 * Remove CWA prefix from entity name and fields
 */
export function removeCwaPrefix(
  data: Record<string, any>,
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    // Remove CWA prefix if it exists
    if (key.startsWith('CWA')) {
      result[key.substring(3)] = value;
    } else {
      // Keep fields without CWA prefix as is
      result[key] = value;
    }
  }

  return result;
}

/**
 * Adds retry mechanism to async operations
 */
export async function executeWithRetry<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    backoffFactor?: number;
    retryableStatusCodes?: number[];
  } = {},
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    backoffFactor = 2,
    retryableStatusCodes = [408, 429, 500, 502, 503, 504],
  } = options;

  let lastError: any;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;

      // Check if we should retry based on error
      const statusCode =
        error.status || (error.response && error.response.status);
      const isRetryable =
        statusCode && retryableStatusCodes.includes(statusCode);

      if (!isRetryable || attempt === maxRetries) {
        throw error;
      }

      // Wait with exponential backoff
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay *= backoffFactor;
    }
  }

  throw lastError;
}

/**
 * Gets the entity name with CWA prefix if necessary
 */
export function getEntityName(
  config: BaseResourceConfig,
  isCwa: boolean = false,
): string {
  const entityName = config.entityName;
  return isCwa && config.supportsCwa ? `CWA_${entityName}` : entityName;
}

/**
 * Gets the appropriate key field name based on mode
 */
export function getKeyField(
  config: BaseResourceConfig,
  isCwa: boolean = false,
): string {
  if (isCwa && config.supportsCwa) {
    // For CWA, use the config keyField with CWA prefix
    return `CWA${config.keyField}`;
  } else if (isListingResourceConfig(config)) {
    // For non-CWA listing resources, use the liveKeyField
    return config.liveKeyField;
  } else {
    // For non-listing resources, just use the keyField
    return config.keyField;
  }
}

export function addCwaPrefixToFieldName(fieldName: string) {
  if (fieldName.startsWith('CWA')) {
    // user already provided the CWA prefix, we can skip this step
    return fieldName;
  }

  return `CWA${fieldName}`;
}

/**
 * Gets the listing key field with appropriate prefix
 */
export function getListingKeyField(
  config: BaseResourceConfig,
  isCwa: boolean = false,
): string | undefined {
  if (!isListingResourceConfig(config)) {
    return undefined;
  }

  return isCwa && config.supportsCwa
    ? addCwaPrefixToFieldName(config.listingKeyField)
    : config.listingKeyField;
}

/**
 * Gets the item number field with appropriate prefix
 */
export function getItemNumberField(
  config: BaseResourceConfig,
  isCwa: boolean = false,
): string | undefined {
  if (!isListingResourceConfig(config) || !config.itemNumberField) {
    return undefined;
  }

  return isCwa && config.supportsCwa
    ? addCwaPrefixToFieldName(config.itemNumberField)
    : config.itemNumberField;
}

/**
 * Gets the LIS key field (only relevant in CWA mode)
 */
export function getLisKeyField(
  config: BaseResourceConfig,
  isCwa: boolean = false,
): string | undefined {
  if (!isListingResourceConfig(config) || !config.lisKeyField) {
    return undefined;
  }

  // LIS key is only meaningful in CWA mode
  return isCwa && config.supportsCwa ? config.lisKeyField : undefined;
}

/**
 * Constructs an OData filter from a set of field/value pairs
 */
export function buildODataFilter(filters: Record<string, any>): string {
  const conditions = Object.entries(filters).map(([field, value]) => {
    // Handle different value types
    if (typeof value === 'string') {
      return `${field} eq '${value}'`;
    } else if (value === null) {
      return `${field} eq null`;
    } else {
      return `${field} eq ${value}`;
    }
  });

  return conditions.join(' and ');
}

/**
 * Execute OData query with automatic pagination using $skip and $top
 */
export async function executeWithPagination<T>(
  executeQuery: (resource: string, options: any) => Promise<any>,
  resource: string,
  queryOptions: any,
  pageSize?: number,
): Promise<{ value: T[]; '@odata.count'?: number }> {
  if (!pageSize) {
    // No pagination requested, execute normally
    return executeQuery(resource, queryOptions);
  }

  const allResults: T[] = [];
  let totalCount: number | undefined;
  let skip = 0;

  // Ensure queryParams exists
  const baseQueryParams = queryOptions.queryParams || {};

  // Add $count=true to get total count on first request
  const firstPageParams = {
    ...baseQueryParams,
    $count: true,
    $top: pageSize,
    $skip: 0,
  };

  // Execute first request to get count
  const firstResponse = await executeQuery(resource, {
    ...queryOptions,
    queryParams: firstPageParams,
  });

  if (firstResponse?.value && Array.isArray(firstResponse.value)) {
    allResults.push(...firstResponse.value);
  }

  // Get total count from first response
  totalCount = firstResponse?.['@odata.count'];

  // If no count returned or we got all records in first page, return early
  if (!totalCount || allResults.length >= totalCount) {
    return {
      value: allResults,
      '@odata.count': totalCount,
    };
  }

  // Calculate how many more pages we need
  skip = pageSize;

  while (skip < totalCount) {
    const pageParams = {
      ...baseQueryParams,
      $top: pageSize,
      $skip: skip,
      // We don't need $count on subsequent requests
    };

    const response = await executeQuery(resource, {
      ...queryOptions,
      queryParams: pageParams,
    });

    if (response?.value && Array.isArray(response.value)) {
      allResults.push(...response.value);
    }

    // Move to next page
    skip += pageSize;
  }

  return {
    value: allResults,
    '@odata.count': totalCount,
  };
}
